import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Button,
  Divider,
  Icon,
  message,
  Popconfirm,
  Row,
  Switch,
  Table,
  Typography
} from 'antd'
import WebhookForm from './WebhookForm'
import {
  createActivityWebhook,
  deleteActivityWebhook,
  fetchActivityWebhooks,
  showApiError,
  updateActivityWebhook,
  updateActivityWebhookEnabled
} from '../../../../../modules/brandActivityTransaction'
import { RESPONSE_MESSAGE } from '../../../../../utils/constants'

const { Text } = Typography

// URL มี token ลับ — แสดงแค่ host + ต้น webhook id
const maskWebhookUrl = (url) => {
  if (!url) return '-'
  const match = url.match(/^https:\/\/([^/]+)\/api\/webhooks\/(\d+)/)
  if (!match) return '••••'
  return `${match[1]}/api/webhooks/${match[2].slice(0, 4)}…`
}

// ไม่มี brandActivityId (ตอนสร้างกิจกรรม) = พัก webhook ไว้ใน drafts แล้วค่อยบันทึกหลังสร้างกิจกรรม
const WebhookTab = ({ brandActivityId, drafts, onDraftsChange }) => {
  const isDraft = !brandActivityId
  const [fetchedWebhooks, setWebhooks] = useState([])
  const webhooks = isDraft ? drafts || [] : fetchedWebhooks
  const [isLoading, setIsLoading] = useState(false)
  const [visibleForm, setVisibleForm] = useState(false)
  const [formState, setFormState] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [togglingId, setTogglingId] = useState(null)

  async function fetchData() {
    setIsLoading(true)
    try {
      const data = await fetchActivityWebhooks(brandActivityId)
      setWebhooks(data || [])
    } catch (err) {
      showApiError(err)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    setWebhooks([])
    if (!isDraft) fetchData()
  }, [brandActivityId])

  const onClickForm = (record) => {
    setFormState(record || null)
    setVisibleForm(true)
  }

  const onCloseForm = () => {
    setVisibleForm(false)
    setFormState(null)
  }

  async function onOk(body) {
    if (isDraft) {
      onDraftsChange(
        formState
          ? webhooks.map((item) =>
              item.id === formState.id ? { ...item, ...body } : item
            )
          : webhooks.concat({ ...body, id: `draft-${Date.now()}` })
      )
      onCloseForm()
      return
    }
    setIsSaving(true)
    try {
      if (formState) {
        await updateActivityWebhook(brandActivityId, formState.id, body)
      } else {
        await createActivityWebhook(brandActivityId, body)
      }
      message.success(RESPONSE_MESSAGE.SUCCESS)
      onCloseForm()
      fetchData()
    } catch (err) {
      if (showApiError(err) === 404) {
        onCloseForm()
        fetchData()
      }
    }
    setIsSaving(false)
  }

  async function onToggleEnabled(record, enabled) {
    if (isDraft) {
      onDraftsChange(
        webhooks.map((item) =>
          item.id === record.id ? { ...item, enabled } : item
        )
      )
      return
    }
    setTogglingId(record.id)
    try {
      const updated = await updateActivityWebhookEnabled(
        brandActivityId,
        record.id,
        enabled
      )
      setWebhooks((prevList) =>
        prevList.map((item) => (item.id === record.id ? updated : item))
      )
    } catch (err) {
      if (showApiError(err) === 404) {
        fetchData()
      }
    }
    setTogglingId(null)
  }

  async function onDelete(id) {
    if (isDraft) {
      onDraftsChange(webhooks.filter((item) => item.id !== id))
      return
    }
    try {
      await deleteActivityWebhook(brandActivityId, id)
      message.success(RESPONSE_MESSAGE.SUCCESS)
    } catch (err) {
      showApiError(err)
    }
    fetchData()
  }

  const columns = [
    {
      title: 'ชื่อ',
      dataIndex: 'name',
      key: 'name',
      render: (name) => name || '-'
    },
    {
      title: 'URL',
      dataIndex: 'url',
      key: 'url',
      render: (url) => (
        <Text copyable={url ? { text: url } : false}>{maskWebhookUrl(url)}</Text>
      )
    },
    {
      title: 'เปิดใช้งาน',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (enabled, record) => (
        <Switch
          checked={!!enabled}
          loading={togglingId === record.id}
          onChange={(checked) => onToggleEnabled(record, checked)}
        />
      )
    },
    {
      title: 'Action',
      key: 'action',
      render: (text, record) => (
        <span>
          <Button
            type="primary"
            size="small"
            shape="circle"
            icon="edit"
            onClick={() => onClickForm(record)}
          />
          <Divider type="vertical" />
          <Popconfirm
            title="Sure to delete?"
            onConfirm={() => onDelete(record.id)}>
            <Button type="danger" size="small" shape="circle" icon="delete" />
          </Popconfirm>
        </span>
      )
    }
  ]

  return (
    <div>
      <Row type="flex" justify="space-between" align="middle">
        <span>
          {isDraft && 'Webhook จะถูกบันทึกหลังกดบันทึกกิจกรรม'}
        </span>
        <Button type="primary" onClick={() => onClickForm(null)}>
          <Icon type="plus" /> เพิ่ม Webhook
        </Button>
      </Row>
      <Table
        style={{ marginTop: 16 }}
        bordered
        loading={isLoading}
        rowKey={(record) => record.id}
        dataSource={webhooks}
        columns={columns}
        pagination={false}
      />
      <WebhookForm
        visible={visibleForm}
        formState={formState}
        loading={isSaving}
        onOk={onOk}
        onCancel={onCloseForm}
      />
    </div>
  )
}

WebhookTab.propTypes = {
  brandActivityId: PropTypes.number,
  drafts: PropTypes.array,
  onDraftsChange: PropTypes.func
}

export default WebhookTab
